import React, { useEffect } from 'react';
import './labcss/privacy.css';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';

const LAST_UPDATED = 'October 23, 2026';

const Section = ({ id, title, children }) => (
  <>
    <section id={id} className="privacy-card mypadding">
      <h2>{title}</h2> <br />
      {children}
    </section> <br /><br />
  </>
);

const List = ({ items }) => (
  <ul className="terms-list">{items.map((item) => <li className="bodytext" key={item}>{item}</li>)}</ul>
);

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Use | SoapLab';
  }, []);

  return (
    <>
      <div className="sl-page privacy-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Terms of Use' }]} />
          <div className="pageheader-row">
            <div className="pageheading">Terms of Use</div>
          </div>
        </div>

        <div className="privacy-content borderleft">
          <p className="privacy-hero__tagline mypadding">
            Please read these terms carefully, especially section 4 (Soapmaking safety) and section 5 (Your products). Using SoapLab means you agree to them.
          </p>

          <div className="privacy-meta borderbottom mypadding">
            <span>Effective date: {LAST_UPDATED}</span>
            <span>Applies to soaplab.in and all SoapLab tools, content and services.</span>
          </div>
          <br />

          <Section id="acceptance" title="1. About these terms">
            <p className="bodytext">
              SoapLab (“SoapLab”, “we”, “us”) provides online tools and information for people who make soap, including calculators,
              recipes, inventory, production and sales records, labels, articles and a community. By creating an account or using
              SoapLab you agree to these Terms of Use and our <a href="/privacy">Privacy Policy</a>. If you use SoapLab for a business,
              you agree to these terms on its behalf. If you do not agree, please do not use SoapLab.
            </p>
          </Section>

          <Section id="eligibility" title="2. Eligibility and accounts">
            <List items={[
              'You must be at least 18 years old to create an account or use SoapLab’s tools.',
              'You are responsible for keeping your login details secure and for all activity under your account.',
              'Keep your account information accurate and up to date.',
            ]} />
          </Section>

          <Section id="payments" title="3. Subscriptions and payments">
            <List items={[
              'Paid plans renew automatically at the price shown when you subscribe until you cancel.',
              'You can cancel at any time from your account settings. Your paid features continue until the end of the period you have already paid for; they then stop renewing.',
              'Refunds follow our Cancellation & Refund Policy (soaplab.in/refund-policy): you can ask for a full refund within 7 days of any payment. After that, payments are not refunded, including for partly used periods, except where the law requires it.',
              'We will tell you in advance about any price change. A new price applies from your next renewal, and you can cancel before then.',
              'Payments are processed by PayPal for customers outside India, and by Razorpay for customers in India, under their own terms. Prices and billing currency depend on your region: rupees in India, US dollars elsewhere. Your bank may add its own foreign-exchange or international-transaction fees.',
            ]} />
          </Section>

          <Section id="safety" title="4. Soapmaking safety">
            <p className="bodytext">
              <strong>Soapmaking can be dangerous.</strong> Sodium hydroxide and potassium hydroxide (lye) are caustic and can cause severe
              burns, blindness and other serious injury. Fresh soap batter is caustic too. Hot oils and melted soap base can burn, and some
              materials used in soapmaking are flammable or can irritate skin.
            </p> <br />
            <List items={[
              'SoapLab’s calculators, recipes, templates, recipe generator suggestions, cost and price estimates, label drafts, articles and other content are general information and tools. They are not professional, chemical, medical, regulatory or legal advice, and they do not replace your own knowledge, training and checks.',
              'Results depend on the information you enter (such as oils, SAP values, lye purity and weights) and on reference values that vary between suppliers and batches. Results can be wrong. You must check every recipe and calculation yourself before you use it.',
              'You are responsible for working safely: wearing protective equipment, working with good ventilation, handling and storing chemicals safely, keeping children and pets away, following your suppliers’ safety data sheets and instructions, and getting medical help when needed.',
              'Make and test small batches before relying on a recipe. Do not use, sell or give away soap that may be unsafe.',
              'SoapLab does not see, test or supervise how you make soap. As far as the law allows, we are not responsible for any injury, damage or loss that results from making, using, selling or giving away soap or other products (see sections 11 and 12).',
            ]} />
          </Section>

          <Section id="your-products" title="5. Your products and legal compliance">
            <List items={[
              'You are the maker and seller of anything you produce. SoapLab does not make, test, certify, approve or sell your products.',
              'You alone are responsible for your products’ safety, quality and testing, and for following every law that applies where you make or sell them. This includes any licences or registrations, cosmetic and product-safety rules, labelling and packaging rules (such as ingredient lists, net quantity, price and maker details), weights and measures, taxes and consumer law.',
              'You are responsible for every claim you make about your products. Do not claim that a product treats, cures or prevents any disease or medical condition unless the law allows you to.',
              'Ingredient lists, labels, batch sheets, costs and prices produced by SoapLab are drafts based on your data. Check them before you use them.',
            ]} />
          </Section>

          <Section id="community" title="6. Community and shared content">
            <List items={[
              'Recipes, posts, comments, profiles and other content shared by users belong to the people who shared them and reflect their own views. SoapLab does not check them for accuracy or safety. Check any shared recipe in the lye calculator before using it.',
              'You are responsible for what you share. Do not post anything unlawful, infringing or misleading, or anything that encourages unsafe practices. We may remove content, and restrict or close accounts, that break these terms.',
              'When you share content publicly, other users may view and copy it within SoapLab, and you allow SoapLab to host and display it to provide the service.',
            ]} />
          </Section>

          <Section id="use" title="7. Using SoapLab and your data">
            <List items={[
              'Use SoapLab lawfully and in line with these terms.',
              'You keep ownership of the recipes, records and other content you create. You give SoapLab a limited permission to store, process and display it only to provide the service to you.',
              'Keep your own copies of important records. You can export your data from the Backup page.',
              'If you record information about your own customers (for example names or phone numbers on sales), you are responsible for having the right to do so under the privacy laws that apply to you.',
            ]} />
          </Section>

          <Section id="conduct" title="8. Prohibited conduct">
            <List items={[
              'No reverse engineering, scraping, or getting around security measures or plan limits.',
              'No uploading malware, unlawful content or material that infringes someone else’s rights.',
              'No reselling the service or sharing paid features without our permission.',
              'No harassment or abuse of other users, and no attempts to disrupt the service.',
            ]} />
          </Section>

          <Section id="ip" title="9. Intellectual property">
            <p className="bodytext">
              SoapLab’s software, design, trademarks, logos, articles and documentation belong to SoapLab or its licensors. You may use them
              only as part of using SoapLab, and may not copy, republish or modify them without permission, except as these terms allow.
            </p>
          </Section>

          <Section id="availability" title="10. Availability, changes and termination">
            <p className="bodytext">
              We may add, change or remove features, or pause the service for maintenance, and will try to give reasonable notice of
              significant changes. You may stop using SoapLab at any time. We may suspend or close accounts that break these terms or when
              the law requires it. Sections that by their nature should continue (including sections 4, 5, 11, 12 and 13) continue after
              your account ends.
            </p>
          </Section>

          <Section id="disclaimers" title="11. Disclaimers">
            <p className="bodytext">
              SoapLab is provided “as is” and “as available”. To the maximum extent permitted by law, we make no promises that the service,
              its calculations, recipes, reference data, estimates or content are accurate, complete, safe or suitable for your purpose, that
              the service will be uninterrupted or error-free, or that using SoapLab will make your products safe, compliant or profitable.
            </p>
          </Section>

          <Section id="liability" title="12. Limitation of liability">
            <List items={[
              'To the maximum extent permitted by law, SoapLab and its owners, team and suppliers are not liable for: personal injury, illness or damage to property arising from making, using, selling or giving away soap or other products, or from following any recipe, calculation or content; claims by your customers or anyone else about your products; or indirect, incidental, special or consequential losses, or loss of profits, revenue, data or goodwill.',
              'To the maximum extent permitted by law, our total liability for all claims relating to SoapLab is limited to the amount you paid us for SoapLab in the 12 months before the claim.',
              'Nothing in these terms excludes or limits any liability that cannot be excluded or limited by law, or your statutory rights as a consumer.',
            ]} />
          </Section>

          <Section id="indemnity" title="13. Indemnity">
            <p className="bodytext">
              You agree to indemnify and hold harmless SoapLab and its owners and team from any claims, losses, damages and costs
              (including reasonable legal fees) arising from the products you make, use, sell or give away, the content you share,
              or your breach of these terms or of any law.
            </p>
          </Section>

          <Section id="law" title="14. Governing law and disputes">
            <p className="bodytext">
              These terms are governed by the laws of India, and the courts of India have jurisdiction over any dispute, without affecting
              any mandatory consumer rights you have where you live. Please contact us first; most problems can be solved quickly and informally.
            </p>
          </Section>

          <Section id="changes" title="15. Changes to these terms">
            <p className="bodytext">
              We may update these terms. We will change the effective date above and tell you about significant changes in the app or by
              email. If you keep using SoapLab after a change takes effect, you accept the updated terms.
            </p>
          </Section>

          <Section id="general" title="16. General">
            <p className="bodytext">
              These terms, our Privacy Policy and any terms shown at checkout are the whole agreement between you and SoapLab about the
              service. If any part is found unenforceable, the rest still applies. If we do not enforce a term straight away, we have not
              given up the right to enforce it later.
            </p>
          </Section>

          <section className="privacy-card contactcard mypadding">
            <h2>17. Contact</h2> <br />
            <p className="bodytext">If you have questions about these terms, contact us using the Contact form.</p> <br />
            <div className="contact-columns">
              <div>
                <p className='smallheadingtext'>Contact form</p>
                <a href="/contact"> <p className='bodytext'>soaplab.in/contact</p></a>
              </div> <br />
              <div>
                <p className='smallheadingtext'>Mailing</p>
                <p>SoapLab Team<br />Request our current mailing address via the Contact form.</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}
